import { ref } from 'vue';

export const pendingRequests = ref(0);

let currentRequestScope = null;
const scopedControllers = new Map();

export function setCurrentRequestScope(scopeKey) {
    currentRequestScope = scopeKey ?? null;
}

export function cancelRequestScope(scopeKey) {
    if (scopeKey == null) return;
    const set = scopedControllers.get(scopeKey);
    if (!set) return;
    set.forEach((controller) => {
        try { controller.abort(); } catch (_) { /* noop */ }
    });
    scopedControllers.delete(scopeKey);
}

function registerScopedController(config) {
    if (currentRequestScope == null) return;
    if (config.signal) return;
    const controller = new AbortController();
    config.signal = controller.signal;
    config.__requestScope = currentRequestScope;
    config.__scopedController = controller;
    let set = scopedControllers.get(currentRequestScope);
    if (!set) {
        set = new Set();
        scopedControllers.set(currentRequestScope, set);
    }
    set.add(controller);
}

function unregisterScopedController(config) {
    if (!config || !config.__requestScope || !config.__scopedController) return;
    const set = scopedControllers.get(config.__requestScope);
    if (set) {
        set.delete(config.__scopedController);
        if (set.size === 0) scopedControllers.delete(config.__requestScope);
    }
}

export function trackAxiosInstance(axiosInstance) {
    axiosInstance.interceptors.request.use(
        (config) => {
            pendingRequests.value++;
            registerScopedController(config);
            return config;
        },
        (error) => {
            pendingRequests.value = Math.max(0, pendingRequests.value - 1);
            return Promise.reject(error);
        }
    );
    axiosInstance.interceptors.response.use(
        (response) => {
            pendingRequests.value = Math.max(0, pendingRequests.value - 1);
            unregisterScopedController(response.config);
            return response;
        },
        (error) => {
            pendingRequests.value = Math.max(0, pendingRequests.value - 1);
            unregisterScopedController(error?.config);
            return Promise.reject(error);
        }
    );
}

export function waitForNetworkIdle({ idleMs = 400, timeout = 8000, minWait = 0 } = {}) {
    return new Promise((resolve) => {
        const start = Date.now();
        let idleSince = pendingRequests.value === 0 ? start : null;
        const tick = () => {
            const now = Date.now();
            const elapsed = now - start;
            if (elapsed >= timeout) return resolve();
            if (pendingRequests.value === 0) {
                if (idleSince === null) idleSince = now;
                if (now - idleSince >= idleMs && elapsed >= minWait) return resolve();
            } else {
                idleSince = null;
            }
            setTimeout(tick, 50);
        };
        tick();
    });
}
